<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBibleSearch, LANGUAGES } from './useBibleSearch';
import CannedResponseAPI from 'dashboard/api/cannedResponse';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['insert', 'close']);

const { t } = useI18n();

const {
  query,
  selectedLanguage,
  selectedTranslation,
  availableTranslations,
  results,
  isLoading,
  error,
  recent,
  favorites,
  selectLanguage,
  search,
  formatVerse,
  trackRecent,
  isFavorited,
  toggleFavorite,
  clearRecent,
} = useBibleSearch();

const activeTab = ref('search'); // 'search' | 'recent' | 'favorites'
const savingId = ref(null);

function insertVerse(verse) {
  const text = formatVerse(verse);
  trackRecent(verse);
  emit('insert', text);
}

function copyVerse(verse) {
  const text = formatVerse(verse);
  navigator.clipboard.writeText(text);
}

async function saveAsCannedResponse(verse) {
  savingId.value = verse.reference;
  const bookName = verse.reference.split(' ')[0];
  const shortCode = `bible-${bookName.toLowerCase()}-${Date.now()}`.slice(
    0,
    50
  );
  const content = formatVerse(verse);

  try {
    await CannedResponseAPI.create({ short_code: shortCode, content });
    useAlert(`Saved! Use /${shortCode} to insert this verse.`);
  } catch {
    useAlert('Could not save canned response. Try again.');
  } finally {
    savingId.value = null;
  }
}

function onSearchKeydown(e) {
  if (e.key === 'Enter') search();
}
</script>

<template>
  <div
    class="bible-search-panel flex flex-col bg-n-solid-1 border border-n-weak rounded-xl mx-2 mb-2 overflow-hidden max-h-[420px]"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-2.5 bg-woot-500 text-white shrink-0"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-book-open size-4" />
        <span class="text-sm font-semibold">{{
          t('MINISTRY.BIBLE_SEARCH.TITLE')
        }}</span>
      </div>
      <button
        class="text-white/70 hover:text-white transition-colors"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <!-- Tabs -->
    <div class="flex border-b border-n-weak shrink-0">
      <button
        v-for="tab in ['search', 'recent', 'favorites']"
        :key="tab"
        class="flex-1 py-2 text-xs font-medium capitalize transition-colors"
        :class="
          activeTab === tab
            ? 'text-woot-500 border-b-2 border-woot-500'
            : 'text-n-slate-9 hover:text-n-slate-12'
        "
        @click="activeTab = tab"
      >
        {{ tab }}
        <span
          v-if="tab === 'recent' && recent.length"
          class="ml-1 text-[10px] bg-n-slate-3 rounded-full px-1.5"
          >{{ recent.length }}
        </span>
        <span
          v-if="tab === 'favorites' && favorites.length"
          class="ml-1 text-[10px] bg-n-slate-3 rounded-full px-1.5"
          >{{ favorites.length }}
        </span>
      </button>
    </div>

    <!-- Search Tab -->
    <div
      v-if="activeTab === 'search'"
      class="flex flex-col gap-2 p-3 overflow-y-auto"
    >
      <!-- Controls -->
      <div class="flex gap-2">
        <select
          class="flex-1 text-xs rounded-md border border-n-weak bg-n-solid-1 px-2 py-1.5 text-n-slate-12 focus:outline-none focus:border-woot-500"
          :value="selectedLanguage.code"
          @change="
            selectLanguage(LANGUAGES.find(l => l.code === $event.target.value))
          "
        >
          <option v-for="lang in LANGUAGES" :key="lang.code" :value="lang.code">
            {{ lang.label }}
          </option>
        </select>
        <select
          v-model="selectedTranslation"
          class="flex-1 text-xs rounded-md border border-n-weak bg-n-solid-1 px-2 py-1.5 text-n-slate-12 focus:outline-none focus:border-woot-500"
        >
          <option v-for="tr in availableTranslations" :key="tr.id" :value="tr">
            {{ tr.label }}
          </option>
        </select>
      </div>
      <div class="flex gap-2">
        <input
          v-model="query"
          type="text"
          :placeholder="t('MINISTRY.BIBLE_SEARCH.SEARCH_PLACEHOLDER')"
          class="flex-1 text-sm px-3 py-1.5 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
          @keydown="onSearchKeydown"
        />
        <button
          class="px-3 py-1.5 rounded-md bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors disabled:opacity-50 shrink-0"
          :disabled="isLoading || !query.trim()"
          @click="search"
        >
          <span
            v-if="isLoading"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          <span v-else>{{ t('MINISTRY.BIBLE_SEARCH.SEARCH') }}</span>
        </button>
      </div>

      <!-- Error -->
      <p v-if="error" class="text-xs text-red-500 px-1">{{ error }}</p>

      <!-- Results -->
      <div
        v-for="verse in results"
        :key="verse.reference + verse.translation"
        class="rounded-lg border border-n-weak bg-n-slate-1 p-3"
      >
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-xs font-semibold text-woot-500">{{
            verse.reference
          }}</span>
          <div class="flex items-center gap-1">
            <span
              class="text-[10px] px-1.5 py-0.5 rounded-full bg-woot-500/10 text-woot-500 font-medium"
              >{{ verse.translation }}
            </span>
            <span
              v-if="verse.language"
              class="text-[10px] px-1.5 py-0.5 rounded-full bg-n-slate-3 text-n-slate-9"
              >{{ verse.language }}
            </span>
          </div>
        </div>
        <p class="text-xs text-n-slate-11 leading-relaxed mb-2 line-clamp-3">
          {{ '"' + verse.text + '"' }}
        </p>
        <div class="flex gap-1.5 flex-wrap">
          <button
            class="text-xs px-2 py-1 rounded-md bg-woot-500 text-white hover:bg-woot-600 transition-colors font-medium"
            @click="insertVerse(verse)"
          >
            {{ t('MINISTRY.BIBLE_SEARCH.INSERT') }}
          </button>
          <button
            class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-9 hover:text-n-slate-12 hover:border-n-slate-9 transition-colors"
            @click="copyVerse(verse)"
          >
            {{ t('MINISTRY.BIBLE_SEARCH.COPY') }}
          </button>
          <button
            class="text-xs px-2 py-1 rounded-md border border-n-weak transition-colors"
            :class="
              isFavorited(verse)
                ? 'text-amber-500 border-amber-300'
                : 'text-n-slate-9 hover:text-amber-500'
            "
            @click="toggleFavorite(verse)"
          >
            <span
              :class="
                isFavorited(verse) ? 'i-lucide-star-off' : 'i-lucide-star'
              "
              class="size-3"
            />
          </button>
          <button
            class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-9 hover:text-n-slate-12 transition-colors disabled:opacity-50"
            :disabled="savingId === verse.reference"
            @click="saveAsCannedResponse(verse)"
          >
            <span
              v-if="savingId === verse.reference"
              class="i-lucide-loader-2 size-3 animate-spin"
            />
            <span v-else>{{ t('MINISTRY.BIBLE_SEARCH.SAVE_CANNED') }}</span>
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-if="!isLoading && !error && results.length === 0"
        class="flex flex-col items-center py-6 text-center text-n-slate-8"
      >
        <span class="i-lucide-book-open size-8 mb-2" />
        <p class="text-xs">{{ t('MINISTRY.BIBLE_SEARCH.SEARCH_HINT') }}</p>
      </div>
    </div>

    <!-- Recent Tab -->
    <div
      v-else-if="activeTab === 'recent'"
      class="flex flex-col gap-2 p-3 overflow-y-auto"
    >
      <div
        v-if="recent.length === 0"
        class="flex flex-col items-center py-6 text-n-slate-8"
      >
        <span class="i-lucide-clock size-8 mb-2" />
        <p class="text-xs">{{ t('MINISTRY.BIBLE_SEARCH.RECENT_EMPTY') }}</p>
      </div>
      <div v-else>
        <div class="flex justify-end mb-2">
          <button
            class="text-xs text-n-slate-8 hover:text-red-500 transition-colors"
            @click="clearRecent"
          >
            {{ t('MINISTRY.BIBLE_SEARCH.CLEAR_ALL') }}
          </button>
        </div>
        <div
          v-for="verse in recent"
          :key="verse.reference + verse.savedAt"
          class="rounded-lg border border-n-weak bg-n-slate-1 p-3 mb-2"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs font-semibold text-woot-500">{{
              verse.reference
            }}</span>
            <span
              class="text-[10px] px-1.5 py-0.5 rounded-full bg-woot-500/10 text-woot-500"
              >{{ verse.translation }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 line-clamp-2 mb-2">
            {{ '"' + verse.text + '"' }}
          </p>
          <div class="flex gap-1.5">
            <button
              class="text-xs px-2 py-1 rounded-md bg-woot-500 text-white hover:bg-woot-600 transition-colors font-medium"
              @click="insertVerse(verse)"
            >
              {{ t('MINISTRY.BIBLE_SEARCH.INSERT') }}
            </button>
            <button
              class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-9 hover:text-n-slate-12 transition-colors"
              @click="copyVerse(verse)"
            >
              {{ t('MINISTRY.BIBLE_SEARCH.COPY') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Favorites Tab -->
    <div
      v-else-if="activeTab === 'favorites'"
      class="flex flex-col gap-2 p-3 overflow-y-auto"
    >
      <div
        v-if="favorites.length === 0"
        class="flex flex-col items-center py-6 text-n-slate-8"
      >
        <span class="i-lucide-star size-8 mb-2" />
        <p class="text-xs">{{ t('MINISTRY.BIBLE_SEARCH.FAVORITES_EMPTY') }}</p>
      </div>
      <div
        v-for="verse in favorites"
        :key="verse.reference + verse.savedAt"
        class="rounded-lg border border-n-weak bg-n-slate-1 p-3 mb-2"
      >
        <div class="flex items-center justify-between mb-1">
          <span class="text-xs font-semibold text-woot-500">{{
            verse.reference
          }}</span>
          <div class="flex items-center gap-1">
            <span
              class="text-[10px] px-1.5 py-0.5 rounded-full bg-woot-500/10 text-woot-500"
              >{{ verse.translation }}
            </span>
            <button
              class="text-amber-500 hover:text-n-slate-9 transition-colors"
              @click="toggleFavorite(verse)"
            >
              <span class="i-lucide-star size-3" />
            </button>
          </div>
        </div>
        <p class="text-xs text-n-slate-11 line-clamp-2 mb-2">
          {{ '"' + verse.text + '"' }}
        </p>
        <div class="flex gap-1.5">
          <button
            class="text-xs px-2 py-1 rounded-md bg-woot-500 text-white hover:bg-woot-600 transition-colors font-medium"
            @click="insertVerse(verse)"
          >
            {{ t('MINISTRY.BIBLE_SEARCH.INSERT') }}
          </button>
          <button
            class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-9 hover:text-n-slate-12 transition-colors"
            @click="copyVerse(verse)"
          >
            {{ t('MINISTRY.BIBLE_SEARCH.COPY') }}
          </button>
          <button
            class="text-xs px-2 py-1 rounded-md border border-n-weak text-n-slate-9 hover:text-n-slate-12 transition-colors disabled:opacity-50"
            :disabled="savingId === verse.reference"
            @click="saveAsCannedResponse(verse)"
          >
            {{ t('MINISTRY.BIBLE_SEARCH.SAVE_CANNED') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
